/**
 * 组织结构相关API服务
 * 包含组织结构的增删查改、状态切换等功能
 */

import { fetcher } from '../base/fetcher'
import type { 
  OrganizationListResponse, 
  GetOrganizationListRequest,
  SaveOrUpdateOrganizationRequest,
  DepartmentListResponse,
  GetDepartmentListRequest,
  SaveOrUpdateDepartmentRequest,
  DepartmentTreeResponse,
  GetDepartmentTreeRequest,
  GetDepartmentTreeToAgentRequest,
  DepartmentTreeToAgentResponse,
  MemberListResponse,
  GetMemberListRequest,
  MoveMembersRequest,
  SaveOrUpdateMemberRequest,
  DownloadTemplateResponse,
  ImportDepartmentRequest,
  ImportMemberRequest,
} from '../types/organization.type'

/**
 * 组织结构相关API路径常量
 */
export const ORGANIZATION_API_PATHS = {
  /** 获取组织列表 */
  LIST: '/team/list',
  /** 删除组织 */
  DELETE: '/team/delById',
  /** 新增/编辑组织 */
  SAVE_OR_UPDATE: '/team/saveOrUpdate',
  /** 切换组织状态 */
  TOGGLE_STATE: '/team/toggleState',
  /** 删除部门 */
  DEPT_DELETE: '/team/department/delById',
  /** 部门列表 */
  DEPT_LIST: '/team/department/list',
  /** 新增或编辑部门 */
  DEPT_SAVE_OR_UPDATE: '/team/department/saveOrUpdate',
  /** 部门树 */
  DEPT_TREE: '/team/department/tree',
  /** 智能体下部门树 */
  DEPT_TREE_TO_AGENT: '/team/department/treeToAgent',
  /** 下载部门导入模板 */
  DEPT_IMPORT_TEMPLATE: '/team/department/download/template',
  /** 导入部门 */
  DEPT_IMPORT: '/team/department/import',
  /** 下载成员导入模板 */
  MEMBER_IMPORT_TEMPLATE: '/team/member/download/template',
  /** 导入成员 */
  MEMBER_IMPORT: '/team/member/import',
  /** 成员：批量删除 */
  MEMBER_DELETE_BATCH: '/team/member/delByIds',
  /** 成员：列表 */
  MEMBER_LIST: '/team/member/list',
  /** 成员：组织下所有成员列表 */
  ALL_MEMBER_LIST: '/team/member/listByTeam',
  /** 成员：移动 */
  MEMBER_MOVE: '/team/member/move',
  /** 成员：新增或编辑 */
  MEMBER_SAVE_OR_UPDATE: '/team/member/saveOrUpdate',
} as const

/**
 * 组织结构服务对象
 * 提供所有组织结构相关的API调用方法
 */
export const organizationService = {
  /**
   * 获取组织列表
   * @param params 查询参数（工作空间ID、搜索关键词、创建人等）
   * @returns 组织列表响应
   */
  getOrganizationList: (params: GetOrganizationListRequest): Promise<OrganizationListResponse> => {
    // 将参数转换为字符串类型以符合fetcher要求
    const stringParams: Record<string, string> = {}
    if (params.workspaceId) stringParams.workspaceId = params.workspaceId
    if (params.search) stringParams.search = params.search
    if (params.creatorId) stringParams.creatorId = params.creatorId

    return fetcher.get<OrganizationListResponse>(ORGANIZATION_API_PATHS.LIST, {
      params: stringParams,
    })
  },

  /**
   * 删除组织
   * @param id 组织ID
   * @returns 删除操作结果
   */
  deleteOrganization: (id: string): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.DELETE, { id }),

  /**
   * 新增或编辑组织
   * @param data 组织信息（新增时不传id，编辑时必传id）
   * @returns 操作结果
   */
  saveOrUpdateOrganization: (data: SaveOrUpdateOrganizationRequest): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.SAVE_OR_UPDATE, data),

  /**
   * 切换组织状态
   * @param id 组织ID
   * @param status 组织状态
   * @returns 切换操作结果
   */
  toggleOrganizationState: (id: string, status: number): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.TOGGLE_STATE, { id, status }),

  /**
   * 获取部门列表
   * @param params 查询参数（父ID、组织ID）
   * @returns 部门列表响应
   */
  getDepartmentList: (params: GetDepartmentListRequest): Promise<DepartmentListResponse> => {
    const stringParams: Record<string, string> = {}
    if (params.parentId) stringParams.parentId = params.parentId
    if (params.teamId) stringParams.teamId = params.teamId
    return fetcher.get<DepartmentListResponse>(ORGANIZATION_API_PATHS.DEPT_LIST, {
      params: stringParams,
    })
  },

  /**
   * 新增或编辑部门
   * @param data 部门信息（新增时不传id，编辑时必传id）
   * @returns 操作结果
   */
  saveOrUpdateDepartment: (data: SaveOrUpdateDepartmentRequest): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.DEPT_SAVE_OR_UPDATE, data),

  /**
   * 删除部门
   * @param id 部门ID
   * @returns 删除操作结果
   */
  deleteDepartment: (id: string): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.DEPT_DELETE, { id }),

  /**
   * 获取部门树
   * @param params 请求参数（组织ID）
   * @returns 部门树响应
   */
  getDepartmentTree: (params: GetDepartmentTreeRequest): Promise<DepartmentTreeResponse> => {
    const stringParams: Record<string, string> = {}
    if (params.teamId) stringParams.teamId = params.teamId
    return fetcher.get<DepartmentTreeResponse>(ORGANIZATION_API_PATHS.DEPT_TREE, {
      params: stringParams,
    })
  },

  /**
   * 获取智能体下部门树
   * @param params 请求参数（智能体表ID、搜索字段、组织ID、工作空间ID）
   * @returns 智能体下部门树响应
   */
  getDepartmentTreeToAgent: (params: GetDepartmentTreeToAgentRequest): Promise<DepartmentTreeToAgentResponse> => {
    const stringParams: Record<string, string> = {}
    if (params.agentTableId) stringParams.agentTableId = params.agentTableId
    if (params.search) stringParams.search = params.search
    if (params.teamId) stringParams.teamId = params.teamId
    if (params.workspaceId) stringParams.workspaceId = params.workspaceId
    return fetcher.get<DepartmentTreeToAgentResponse>(ORGANIZATION_API_PATHS.DEPT_TREE_TO_AGENT, {
      params: stringParams,
    })
  },

  /**
   * 获取成员列表
   */
  getMemberList: (params: GetMemberListRequest, isRoot: boolean): Promise<MemberListResponse> => {
    const stringParams: Record<string, string> = {}
    if (params.departmentId && !isRoot) stringParams.departmentId = params.departmentId
    if (params.teamId && isRoot) stringParams.teamId = params.teamId
    if (params.page) stringParams.page = params.page
    if (typeof params.pageSize === 'number') stringParams.pageSize = String(params.pageSize)
    if (params.search) stringParams.search = params.search
    const url = isRoot ? ORGANIZATION_API_PATHS.ALL_MEMBER_LIST : ORGANIZATION_API_PATHS.MEMBER_LIST
    return fetcher.get<MemberListResponse>(url, {
      params: stringParams,
    })
  },

  /**
   * 批量删除成员
   */
  deleteMembers: (ids: string[]): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.MEMBER_DELETE_BATCH, { ids }),

  /**
   * 移动成员
   */
  moveMembers: (payload: MoveMembersRequest): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.MEMBER_MOVE, payload),

  /**
   * 新增或编辑成员
   */
  saveOrUpdateMember: (payload: SaveOrUpdateMemberRequest): Promise<boolean> => 
    fetcher.post<boolean>(ORGANIZATION_API_PATHS.MEMBER_SAVE_OR_UPDATE, payload),

  /**
   * 下载部门导入模板
   */
  downloadDepartmentTemplate: async (): Promise<DownloadTemplateResponse> => {
    const result = await fetcher.download(ORGANIZATION_API_PATHS.DEPT_IMPORT_TEMPLATE)
    return {
      file: result.data,
      filename: decodeURIComponent(result.filename),
      description: result.description || '部门导入模板'
    }
  },

  /**
   * 导入部门
   */
  importDepartment: (payload: ImportDepartmentRequest): Promise<boolean> => {
    const formData = new FormData()
    formData.append('teamId', payload.teamId)
    formData.append('file', payload.file)
    // 不要设置任何 headers，让 fetcher 处理
    return fetcher.post<boolean>(ORGANIZATION_API_PATHS.DEPT_IMPORT, formData)
  },

  /**
   * 下载成员导入模板
   */
  downloadMemberTemplate: async (): Promise<DownloadTemplateResponse> => {
    const result = await fetcher.download(ORGANIZATION_API_PATHS.MEMBER_IMPORT_TEMPLATE)
    return {
      file: result.data,
      filename: decodeURIComponent(result.filename),
      description: result.description || '成员导入模板'
    }
  },

  /**
   * 导入成员
   */
  importMember: (payload: ImportMemberRequest): Promise<boolean> => {
    const formData = new FormData()
    formData.append('departmentId', String(payload.departmentId))
    formData.append('teamId', String(payload.teamId))
    formData.append('workspaceId', String(payload.workspaceId))
    formData.append('file', payload.file)
    // 不要设置任何 headers，让 fetcher 处理
    return fetcher.post<boolean>(ORGANIZATION_API_PATHS.MEMBER_IMPORT, formData)
  },
}
